/**
 * API client for Beauty Checkup backend.
 *
 * Wraps `fetch` with:
 * - automatic base URL resolution (via Vite proxy -> /api)
 * - JWT Authorization header injection
 * - JSON body serialization
 * - typed error handling
 * - offline-aware: opt-in queueing of mutations when offline
 */

import { addToSyncQueue } from '@/lib/offline';

const BASE_URL = '/api';

// ---------------------------------------------------------------------------
// Token storage – mirrors what AuthContext manages, but keeps the api module
// decoupled so it can be imported anywhere without circular deps.
// ---------------------------------------------------------------------------

let _accessToken: string | null = null;

export function setAccessToken(token: string | null) {
  _accessToken = token;
}

export function getAccessToken() {
  return _accessToken;
}

// ---------------------------------------------------------------------------
// Error type
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;

  constructor(
    message: string,
    status: number,
    code?: string,
    details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// ---------------------------------------------------------------------------
// Core fetch wrapper
// ---------------------------------------------------------------------------

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skip automatic JSON parsing (e.g. for blob responses). */
  raw?: boolean;
  /**
   * When true and the device is offline:
   * - GET: return cached response from Service Worker if available
   * - POST/PATCH/PUT/DELETE: queue in IndexedDB sync queue for later replay
   *
   * Defaults to false — offline queueing is opt-in.
   */
  offlineSupport?: boolean;
}

async function request<T = unknown>(
  endpoint: string,
  { body, raw, offlineSupport, headers: customHeaders, ...init }: RequestOptions = {},
): Promise<T> {
  const headers = new Headers(customHeaders);

  if (_accessToken) {
    headers.set('Authorization', `Bearer ${_accessToken}`);
  }

  if (body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const method = (init.method ?? 'GET').toUpperCase();

  // ---- Offline handling ----
  if (offlineSupport && !navigator.onLine) {
    if (method !== 'GET') {
      // Queue mutation for later replay
      await addToSyncQueue({
        method: method as 'POST' | 'PATCH' | 'PUT' | 'DELETE',
        url: endpoint,
        body,
        headers: headersToRecord(headers),
        createdAt: Date.now(),
        retries: 0,
        maxRetries: 3,
      });

      // Register for background sync if available
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        try {
          const reg = await navigator.serviceWorker.ready;
          if ('sync' in reg) {
            await (reg as unknown as { sync: { register: (tag: string) => Promise<void> } }).sync.register('sync-queue');
          }
        } catch {
          // Background sync not supported — will retry via periodic sync
        }
      }

      // Return a synthetic "accepted" response so the caller can continue
      return undefined as T;
    }
    // For GET requests when offline, we still try fetch (Service Worker may serve from cache)
  }

  // ---- Normal fetch ----
  try {
    const res = await fetch(`${BASE_URL}${endpoint}`, {
      ...init,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    // ----- Handle non-OK responses -----
    if (!res.ok) {
      let parsed: { message?: string; code?: string; details?: unknown } = {};
      try {
        parsed = await res.json();
      } catch {
        // response body isn't JSON – fall through
      }

      throw new ApiError(
        parsed.message ?? res.statusText ?? 'Errore di rete',
        res.status,
        parsed.code,
        parsed.details,
      );
    }

    // 204 No Content
    if (res.status === 204) return undefined as T;

    if (raw) return res as unknown as T;

    return res.json() as Promise<T>;
  } catch (err) {
    // If offline and offlineSupport is enabled for a mutation, queue it
    if (
      offlineSupport &&
      method !== 'GET' &&
      err instanceof TypeError &&
      !navigator.onLine
    ) {
      await addToSyncQueue({
        method: method as 'POST' | 'PATCH' | 'PUT' | 'DELETE',
        url: endpoint,
        body,
        headers: headersToRecord(headers),
        createdAt: Date.now(),
        retries: 0,
        maxRetries: 3,
      });
      return undefined as T;
    }

    throw err;
  }
}

// ---------------------------------------------------------------------------
// Helper: convert Headers to plain object (for IndexedDB storage)
// ---------------------------------------------------------------------------

function headersToRecord(headers: Headers): Record<string, string> {
  const record: Record<string, string> = {};
  headers.forEach((value, key) => {
    // Don't store the auth header — it will be injected fresh at sync time
    if (key.toLowerCase() !== 'authorization') {
      record[key] = value;
    }
  });
  return record;
}

// ---------------------------------------------------------------------------
// Convenience methods
// ---------------------------------------------------------------------------

export const api = {
  get<T = unknown>(endpoint: string, opts?: RequestOptions) {
    return request<T>(endpoint, { ...opts, method: 'GET' });
  },

  post<T = unknown>(endpoint: string, body?: unknown, opts?: RequestOptions) {
    return request<T>(endpoint, { ...opts, method: 'POST', body });
  },

  put<T = unknown>(endpoint: string, body?: unknown, opts?: RequestOptions) {
    return request<T>(endpoint, { ...opts, method: 'PUT', body });
  },

  patch<T = unknown>(endpoint: string, body?: unknown, opts?: RequestOptions) {
    return request<T>(endpoint, { ...opts, method: 'PATCH', body });
  },

  delete<T = unknown>(endpoint: string, opts?: RequestOptions) {
    return request<T>(endpoint, { ...opts, method: 'DELETE' });
  },
} as const;
